<div
  class="ai-panel"
  [class.ai-panel-split]="resultsService.current()"
  [class.ai-panel--add-camera]="isAddCameraOverlay()"
  [class.ai-panel--has-submitted]="hasSubmitted()"
>
  <!-- LEFT: chat column — topbar / transcript / composer.
       Click on a user message to jump the panel to that turn's result. -->
  <div
    class="ai-panel-chat-column"
    [class.ai-panel__chat-column--clickable]="resultsService.totalEntries() > 0"
    (click)="onChatAreaClick($event)"
  >
    <!-- Topbar: exit to plain search, new conversation -->
    <div class="ai-panel-topbar">
      <ui-button
        type="noBorderDefault"
        size="s"
        iconClass="ui-icon-search"
        [text]="'SEARCH_GLOBAL_MODAL.AI_BACK_TO_SEARCH' | translate"
        (clickAction)="onBackToSearch()"
      ></ui-button>
      <ui-button
        *ngIf="hasSubmitted()"
        class="ml-auto"
        type="noBorderDefault"
        size="s"
        [text]="'SEARCH_GLOBAL_MODAL.AI_NEW_CHAT' | translate"
        (clickAction)="newChat()"
      ></ui-button>
      <ui-button
        class="ai-panel-expand-btn"
        type="noBorderDefault"
        size="s"
        iconClass="ui-icon-fullscreen-2"
        [class.ai-panel-expand-btn--expanded]="isExpanded()"
        [attr.aria-label]="isExpanded() ? 'Restore chat window size' : 'Expand chat window size'"
        [attr.aria-pressed]="isExpanded()"
        [title]="isExpanded() ? 'Restore chat window size' : 'Expand chat window size'"
        (clickAction)="toggleExpanded.emit()"
      ></ui-button>
    </div>

    @if (hasRuntimeConnectionError()) {
      <div class="ai-panel-state" role="alert">
        <span class="ui-icon-warning ai-panel-state-icon" aria-hidden="true"></span>
        <div class="ai-panel-state-copy">
          <h2>{{ 'SEARCH_GLOBAL_MODAL.AI_INIT_ERROR_TITLE' | translate }}</h2>
          <p>{{ 'SEARCH_GLOBAL_MODAL.AI_INIT_ERROR_DESCRIPTION' | translate }}</p>
        </div>
        <ui-button
          type="primary"
          size="s"
          [text]="'COMMON.RETRY' | translate"
          (clickAction)="retryRuntimeConnection()"
        ></ui-button>
      </div>
    } @else if (isRuntimeConnecting()) {
      <div class="ai-panel-state" aria-live="polite">
        <ui-preloader
          [diameter]="12"
          [color]="PreloaderColor.blue"
        ></ui-preloader>
        <span>{{ 'SEARCH_GLOBAL_MODAL.AI_INITIALIZING' | translate }}</span>
      </div>
    } @else {
      <!--
        Transcript rail.
        copilot-chat provides the ChatState injection context and renders streamed messages.
        - inputComponent: kept as the empty stub; the real input is the sibling
          app-ai-composer below (see the component doc for why).
        - threadId: changing it resets the CopilotKit conversation thread.
        - *ngIf="isChatMounted()": toggling this destroys/recreates the component so that
          CopilotKit's internal message cache is fully cleared on each new session.
      -->
      <div
        class="ai-panel-transcript"
        #chatCol
        (scroll)="onTranscriptScroll()"
      >
        <copilot-chat
          *ngIf="isChatMounted()"
          #chat
          [threadId]="threadId()"
          [inputComponent]="emptyInput"
          [class.ai-panel__chat--active]="hasSubmitted()"
        >
        </copilot-chat>

        <div class="ai-panel-thinking" *ngIf="isRunning()">
          <ui-preloader
            [diameter]="12"
            [color]="PreloaderColor.blue"
          ></ui-preloader>
          <span>{{ 'SEARCH_GLOBAL_MODAL.AI_THINKING' | translate }}</span>
        </div>
      </div>

      <!-- Bottom-docked composer -->
      <div class="ai-panel-footer">
        <app-ai-composer
          #composer
          [flipFromTop]="composerFlipFromTop()"
          [initialValue]="initialPrompt()"
          [busy]="isRunning()"
          [showSuggestions]="!hasSubmitted()"
          (promptSubmit)="onComposerSubmit($event)"
          (stopRequest)="onComposerStop()"
        ></app-ai-composer>
      </div>
    }
  </div>

  <!-- RIGHT: result panel — shown whenever a tool returns data -->
  <div
    class="ai-panel-result-column"
    *ngIf="resultsService.current()"
  >
    <!--
      Context bar: shows the user question that produced the current panel state,
      plus ‹ › navigation when there are multiple history entries.
    -->
    <div
      *ngIf="!isPickCameraOverlay()"
      class="ai-panel__context-bar"
      [class.ai-panel__context-bar--overlay]="resultsService.isOverlay()"
    >
      <!-- Overlay mode: show a "back" crumb -->
      <ng-container *ngIf="resultsService.isOverlay()">
        <button
          class="ai-panel__overlay-back"
          (click)="resultsService.dismissOverlay()"
          title="Back to question result"
        >
          ‹
        </button>
        <span
          class="ai-panel__context-question ai-panel__context-question--overlay"
          [title]="resultsService.currentLabel()"
        >
          {{ resultsService.currentLabel() }}
        </span>
      </ng-container>

      <!-- Normal history mode -->
      <ng-container *ngIf="!resultsService.isOverlay()">
        <span
          class="ai-panel__context-question"
          [title]="resultsService.currentPrompt()"
        >
          {{ resultsService.currentPrompt() }}
        </span>
        <div
          class="ai-panel__context-nav"
          *ngIf="resultsService.hasMultipleEntries()"
        >
          <button
            class="ai-panel__history-btn"
            [disabled]="!resultsService.hasPrev()"
            (click)="resultsService.navigatePrev()"
            title="Previous result"
          >
            ‹
          </button>
          <span class="ai-panel__history-pos">
            {{ resultsService.currentPosition() }}/{{
              resultsService.totalEntries()
            }}
          </span>
          <button
            class="ai-panel__history-btn"
            [disabled]="!resultsService.hasNext()"
            (click)="resultsService.navigateNext()"
            title="Next result"
          >
            ›
          </button>
        </div>
      </ng-container>

      <!-- Actions the active result component publishes (e.g. open group page, add vehicle to group) -->
      <ng-container *ngFor="let action of resultsService.headerActions()">
        <button
          *ngIf="!action.kind || action.kind === HeaderActionKind.ICON"
          type="button"
          class="open-page-btn"
          [title]="action.tooltip | translate"
          [disabled]="(action.disabled?.() ?? false) || (action.loading?.() ?? false)"
          (click)="action.onClick()"
        >
          <ui-preloader
            *ngIf="action.loading?.()"
            [diameter]="12"
            [color]="PreloaderColor.blue"
          ></ui-preloader>
          <span *ngIf="!action.loading?.()" [class]="action.icon"></span>
        </button>

        <button
          *ngIf="action.kind === HeaderActionKind.BUTTON_DEFAULT || action.kind === HeaderActionKind.BUTTON_PRIMARY"
          type="button"
          class="ai-panel__action-btn"
          [class.ai-panel__action-btn--primary]="action.kind === HeaderActionKind.BUTTON_PRIMARY"
          [class.ai-panel__action-btn--loading]="action.loading?.()"
          [title]="action.tooltip | translate"
          [disabled]="(action.disabled?.() ?? false) || (action.loading?.() ?? false)"
          (click)="action.onClick()"
        >
          <ui-preloader
            *ngIf="action.loading?.()"
            [diameter]="12"
            [color]="action.kind === HeaderActionKind.BUTTON_PRIMARY ? PreloaderColor.white : PreloaderColor.blue"
          ></ui-preloader>
          <span class="ai-panel__action-btn-label">{{ action.label | translate }}</span>
        </button>
      </ng-container>
    </div>

    <div
      class="ai-panel-result-viewport"
      [class.ai-panel-result-viewport--fill]="resultLayout() === 'fill'"
      [class.ai-panel-result-viewport--content]="resultLayout() === 'content'"
    >
    <ng-container [ngSwitch]="resultsService.current()?.type">
      <app-organization-members-result *ngSwitchCase="'organization-members'">
      </app-organization-members-result>

      <app-user-organizations-result *ngSwitchCase="'user-organizations'">
      </app-user-organizations-result>

      <app-tag-result *ngSwitchCase="'tags'">
      </app-tag-result>

      <app-event-tags-result *ngSwitchCase="'event-tags'">
      </app-event-tags-result>

      <app-create-event-tag-result *ngSwitchCase="createEventTagResultType">
      </app-create-event-tag-result>

      <app-time-schedule-result *ngSwitchCase="'time-schedule'">
      </app-time-schedule-result>

      <app-create-time-schedule-result *ngSwitchCase="createTimeScheduleResultType">
      </app-create-time-schedule-result>

      <app-device-health-result
        *ngSwitchCase="'device-health'"
        [response]="getDeviceHealthResult()"
      >
      </app-device-health-result>

      <app-device-list-result
        *ngSwitchCase="'device-list'"
        [response]="getDeviceListResult()"
      >
      </app-device-list-result>

      <app-locations-result
        *ngSwitchCase="'locations'"
        [response]="getLocationsResult()"
      >
      </app-locations-result>

      <app-pick-camera-result
        *ngSwitchCase="pickCameraResultType"
        [request]="getPickCameraResult()"
        (cameraPicked)="resultsService.resolveCameraPick($event)"
        (camerasPicked)="resultsService.resolveCamerasPick($event)"
      >
      </app-pick-camera-result>

      <app-add-camera-result
        *ngSwitchCase="addCameraResultType"
        [request]="getAddCameraResult()"
      ></app-add-camera-result>
      <app-add-core-result
        *ngSwitchCase="addCoreResultType"
        [request]="getAddCoreResult()"
      ></app-add-core-result>

      <app-persons-result *ngSwitchCase="'persons'"> </app-persons-result>

      <app-person-groups-result *ngSwitchCase="'person-groups'">
      </app-person-groups-result>

      <app-dashboards-result *ngSwitchCase="'dashboards'"> </app-dashboards-result>

      <app-vehicles-result *ngSwitchCase="'vehicles'"> </app-vehicles-result>

      <app-vehicle-groups-result *ngSwitchCase="'vehicle-groups'">
      </app-vehicle-groups-result>

      <app-search-result *ngSwitchCase="'search'"> </app-search-result>

      <app-alert-monitoring-result *ngSwitchCase="'alerts'">
      </app-alert-monitoring-result>

      <app-alert-configurations-result *ngSwitchCase="'alert-configurations'">
      </app-alert-configurations-result>

      <app-alert-templates-result *ngSwitchCase="'alert-templates'">
      </app-alert-templates-result>

      <app-create-alert-result *ngSwitchCase="'create-alert'">
      </app-create-alert-result>

      <app-case-manager-result *ngSwitchCase="'case-manager'">
      </app-case-manager-result>

      <app-case-manager-category-result *ngSwitchCase="'case-manager-category'">
      </app-case-manager-category-result>

      <app-case-manager-feature-result
        *ngSwitchCase="'case-manager-feature'"
        [data]="getCaseManagerFeatureResult()"
      >
      </app-case-manager-feature-result>

      <app-archive-result *ngSwitchCase="'archive'"> </app-archive-result>

      <app-walls-result *ngSwitchCase="'wall'"> </app-walls-result>

      <app-create-wall-result *ngSwitchCase="'create-wall'"> </app-create-wall-result>

      <app-wall-camera-result *ngSwitchCase="'wall-camera'"> </app-wall-camera-result>

      <app-chart-result *ngSwitchCase="'chart'"> </app-chart-result>

      <app-variables-result *ngSwitchCase="'variables'"> </app-variables-result>

      <app-zone-regions-result *ngSwitchCase="'zone-regions'"> </app-zone-regions-result>

      <app-custom-objects-result *ngSwitchCase="'custom-objects'"> </app-custom-objects-result>
    </ng-container>
    </div>
  </div>
</div>
